/**
 * 크레딧·결제 가시성 공용 타입 (aivatar Phase 0 / PR #379 · #411 · #412)
 *
 * 백엔드 enum 을 그대로 옮긴다. memo 문자열로 출처를 추정하면 memo 표기가 바뀔 때
 * 분류가 조용히 틀리므로, 화면은 항상 원본 enum 값만 읽는다.
 */

/** `transaction.credit_source` 4계열. 사용 차감 행과 백필 이전 행은 null 이다 */
export type CreditSource = 'SUBSCRIPTION' | 'SINGLE_PAYMENT' | 'ADMIN_GRANT' | 'COUPON'

export const CREDIT_SOURCE_LABEL: Record<CreditSource, string> = {
  SUBSCRIPTION: '구독',
  SINGLE_PAYMENT: '단건',
  ADMIN_GRANT: '어드민',
  COUPON: '쿠폰',
}

/**
 * 원장 행의 변동 유형. 부호 + 출처로 파생한다 (원장에 유형 컬럼이 없다).
 *
 * '소멸'·'회수'·'반환' 세분화는 Phase 0 데이터로 불가하다. 소멸과 회수는 원장에서
 * `amount < 0` + `creditSource != null` 로 완전히 같은 모양이고, 반환(환불)은 원장에
 * 행을 만들지 않는다(`invoice_refund` 가 전담).
 */
export type CreditChangeType = 'ISSUE' | 'ISSUE_UNTAGGED' | 'CONSUME' | 'RECALL_OR_EXPIRE' | 'ZERO'

export const CREDIT_CHANGE_TYPE_LABEL: Record<CreditChangeType, string> = {
  ISSUE: '지급',
  ISSUE_UNTAGGED: '지급(미태깅)',
  CONSUME: '차감(사용)',
  RECALL_OR_EXPIRE: '회수·소멸',
  ZERO: '-',
}

export const resolveCreditChangeType = (amount?: number | null, creditSource?: CreditSource | null): CreditChangeType => {
  const value = Number(amount ?? 0)

  if (value > 0) return creditSource ? 'ISSUE' : 'ISSUE_UNTAGGED'
  if (value < 0) return creditSource ? 'RECALL_OR_EXPIRE' : 'CONSUME'
  return 'ZERO'
}

/** `GET /v1/workspaces/{id}/receipts` 행 */
export type TransactionReceipt = {
  receiptId: number
  transactionId: number | null
  amount: number
  creditSource: CreditSource | null
  memo: string | null
  createdAt: string
  updatedAt: string
}

/** 출처별 잔량. 폴백(체크포인트 미존재) 워크스페이스는 204 라서 이 객체 자체가 없다 */
export type CreditPoolBalance = {
  subscription: number
  adminGrant: number
  coupon: number
  singlePayment: number
  /** 출처를 못 찾은 사용분. 0 이 아니면 잔량을 그대로 믿으면 안 된다 */
  unappliedConsumption: number
  /** 출처 미태깅 지급분. 0 이 아니면 잔량을 그대로 믿으면 안 된다 */
  untaggedIssuance: number
}

/**
 * 분해 근거. 차이는 정확도가 아니라 **범위**다 (aivatar #413).
 *
 * - `CHECKPOINT` — 확정값
 * - `FALLBACK` — 현행 윈도우 재생값. `windowStart` 이전에 부여된 잔량은 잡히지 않는다.
 *   현행 잔액 계산도 같은 윈도우를 보므로 화면 잔액과 세계관은 일치하고, 어드민 회수 상한이
 *   쓰는 값도 이것이다. 무료 플랜은 갱신이 없어 이 경로가 항구적이다.
 */
export type CreditPoolBasis = 'CHECKPOINT' | 'FALLBACK'

/**
 * `GET /v1/admin/workspaces/{id}/credit-pools` 응답.
 *
 * `basis`·`windowStart`·`total`·`hasAnomaly` 는 #413 에서 추가됐다. 배포 전 응답에는 없으므로
 * 옵셔널로 두고, 없을 때만 화면에서 합계·이상치를 계산한다.
 */
export type CreditPoolView = {
  basis?: CreditPoolBasis
  windowStart?: string | null
  pools: CreditPoolBalance
  total?: number
  hasAnomaly?: boolean
}

/** 백엔드 `CreditPoolBalance.total()` 과 같은 식. 서버가 `total` 을 주지 않을 때만 쓴다 */
export const creditPoolTotal = (pools: CreditPoolBalance) =>
  Number(pools.subscription) + Number(pools.adminGrant) + Number(pools.coupon) + Number(pools.singlePayment)

/** 백엔드 `hasAnomaly()` 와 동일 조건. 서버가 `hasAnomaly` 를 주지 않을 때만 쓴다 */
export const hasCreditPoolAnomaly = (pools: CreditPoolBalance) =>
  Number(pools.unappliedConsumption) !== 0 || Number(pools.untaggedIssuance) !== 0

export type TransactionScheduleStatus = 'PENDING' | 'COMPLETED' | 'FAILED' | 'CANCELLED'

export const ALL_SCHEDULE_STATUSES: TransactionScheduleStatus[] = ['PENDING', 'COMPLETED', 'FAILED', 'CANCELLED']

export const SCHEDULE_STATUS_LABEL: Record<TransactionScheduleStatus, string> = {
  PENDING: '미실행',
  COMPLETED: '실행',
  FAILED: '실패',
  CANCELLED: '취소',
}

export const SCHEDULE_STATUS_COLOR: Record<TransactionScheduleStatus, string> = {
  PENDING: 'default',
  COMPLETED: 'green',
  FAILED: 'red',
  CANCELLED: 'orange',
}

/** `GET /v1/workspaces/{id}/scheduleCredits` 행. 실행 결과 3필드는 COMPLETED 에만 채워진다 */
export type TransactionSchedule = {
  id: number
  workspaceId: number
  amount: number
  memo: string | null
  scheduledAt: string
  issuerId: number | null
  issuerEmail: string | null
  status: TransactionScheduleStatus
  transactionId: number | null
  executedAmount: number | null
  shortfall: number | null
}

/** `SubscriptionStatus` 와 다른 축이다. 상태1 워크스페이스의 current 구독 status 는 PAUSE 다 */
export type WorkspaceActivityState = 'ACTIVE' | 'INACTIVE_1' | 'INACTIVE_2' | 'INACTIVE_3' | 'UNDETERMINED'

export const ACTIVITY_STATE_LABEL: Record<WorkspaceActivityState, string> = {
  ACTIVE: '활성',
  INACTIVE_1: '상태1 (비활성)',
  INACTIVE_2: '상태2 (3개월 경과)',
  INACTIVE_3: '상태3 (12개월 경과)',
  UNDETERMINED: '판정 불가',
}

export const ACTIVITY_STATE_COLOR: Record<WorkspaceActivityState, string> = {
  ACTIVE: 'green',
  INACTIVE_1: 'gold',
  INACTIVE_2: 'orange',
  INACTIVE_3: 'red',
  UNDETERMINED: 'default',
}

export type WorkspaceActivityView = {
  state: WorkspaceActivityState
  /** 비활성 기준일(PAUSE 구독 startedAt). 비활성 3단계에만 값이 있다 */
  inactiveSince: string | null
  /** 상태1 은 3개월 시점, 상태2 는 12개월 시점. 상태3 은 상한이 없어 null */
  nextStateAt: string | null
  /** 판정 기준일. 일 단위이며 시각이 아니다 */
  evaluatedAt: string
}
